import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { toast } from "sonner";
import { Send, Undo2, XCircle, FileText } from "lucide-react";
import { Button } from "@/components/ui/button";
import { api, errMsg, fmtQty, fmtDT, fmtINR, openAttachment } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, StatusBadge, tableWrap, th, td, num } from "@/components/common";

export default function InwardDetail() {
  const { id } = useParams();
  const { can } = useAuth();
  const [d, setD] = useState(null);
  const load = () => api.get(`/inwards/${id}`).then((r) => setD(r.data));
  useEffect(() => { load(); }, [id]); // eslint-disable-line react-hooks/exhaustive-deps
  if (!d) return null;
  const act = async (path, msg, needReason) => {
    let body;
    if (needReason) {
      const reason = window.prompt("Reason (required, recorded in audit log):");
      if (!reason) return;
      body = { reason };
    }
    try { await api.post(`/inwards/${id}/${path}`, body); toast.success(msg); load(); } catch (e) { toast.error(errMsg(e)); }
  };
  const info = [["Date", d.date], ["Supplier", d.supplier_name], ["Invoice No", d.invoice_no], ["PO No", d.po_no], ["Vehicle", d.vehicle_no], ["Transporter", d.transporter], ["LR No", d.lr_no],
    ["Warehouse", d.warehouse], ["Received By", d.received_by], ["Verified By", d.verified_by], ["Entered By", d.entered_by?.name], ["Posted By", d.posted_by ? `${d.posted_by.name} · ${fmtDT(d.posted_at)}` : "—"], ["Remarks", d.remarks]];
  return (
    <div className="space-y-6">
      <PageHeader title={<span className="font-mono">{d.inward_no}</span>} subtitle={`Created ${fmtDT(d.created_at)}`}>
        <StatusBadge status={d.status} testid="inward-detail-status" />
        {d.status === "submitted" && can("inward:post") && <>
          <Button className="bg-red-600 hover:bg-red-700 text-white" onClick={() => act("post", "Posted — stock updated")} data-testid="inward-post-btn"><Send className="h-4 w-4 mr-1" />Post to stock</Button>
          <Button variant="outline" onClick={() => act("cancel", "Entry cancelled", true)} data-testid="inward-cancel-entry-btn"><XCircle className="h-4 w-4 mr-1" />Cancel</Button>
        </>}
        {d.status === "posted" && can("inward:post") && <Button variant="outline" onClick={() => act("reverse", "Entry reversed", true)} data-testid="inward-reverse-btn"><Undo2 className="h-4 w-4 mr-1" />Reverse</Button>}
      </PageHeader>
      <div className="rounded-lg border bg-card p-4 grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
        {info.map(([k, v]) => <div key={k}><div className="text-[11px] uppercase tracking-wider text-muted-foreground">{k}</div><div className="font-medium break-words">{v || "—"}</div></div>)}
      </div>
      <div className={tableWrap}>
        <table className="w-full" data-testid="inward-lines-table">
          <thead><tr>{["#", "SKU", "Brand", "Cable Size", "Drum No", "Qty", ...(can("cost:view") ? ["Rate"] : []), "Rack"].map((h) => <th key={h} className={th}>{h}</th>)}</tr></thead>
          <tbody>{d.lines.map((l, i) => (
            <tr key={i}><td className={td}>{i + 1}</td><td className={`${td} font-mono`}>{l.sku}</td><td className={td}>{l.brand}</td><td className={td}>{l.cable_size}</td><td className={`${td} font-mono`}>{l.drum_no}</td>
              <td className={`${td} ${num}`}>{fmtQty(l.quantity, l.unit)}</td>{can("cost:view") && <td className={`${td} ${num}`}>{fmtINR(l.rate)}</td>}<td className={td}>{l.rack}</td></tr>
          ))}</tbody>
        </table>
      </div>
      {d.drums.length > 0 && (
        <div>
          <div className="text-[11px] uppercase tracking-wider font-semibold text-muted-foreground mb-2">Drums created</div>
          <div className="flex flex-wrap gap-2">{d.drums.map((dr) => <Link key={dr.id} to={`/drums/${dr.id}`} className="rounded border px-3 py-1.5 text-sm font-mono hover:bg-muted" data-testid={`inward-drum-${dr.drum_code}`}>{dr.drum_code} · {fmtQty(dr.current_balance, dr.uom)}</Link>)}</div>
        </div>
      )}
      <div>
        <div className="text-[11px] uppercase tracking-wider font-semibold text-muted-foreground mb-2">Attachments</div>
        <div className="flex flex-wrap gap-2">
          {d.attachments.map((a) => <button key={a.id} onClick={() => openAttachment(a.id).catch((e) => toast.error(errMsg(e)))} className="inline-flex items-center gap-1 rounded border px-3 py-1.5 text-sm hover:bg-muted" data-testid={`inward-attachment-${a.id}`}><FileText className="h-4 w-4" />{a.filename}</button>)}
          {!d.attachments.length && <span className="text-sm text-muted-foreground">None</span>}
        </div>
      </div>
    </div>
  );
}
